class AntifoulingQuantityCalculator {
  constructor(root) {
    this.root = root;
    if (this.root.dataset.afQuantityInitialized === 'true') return;
    this.root.dataset.afQuantityInitialized = 'true';

    this.form = this.root.querySelector('form');
    this.error = this.root.querySelector('[role="alert"]');
    this.locale = this.root.dataset.locale === 'en' ? 'en' : 'de';
    this.formatNumber = new Intl.NumberFormat(this.locale === 'de' ? 'de-DE' : 'en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate(true);
    });

    this.form.addEventListener('input', () => this.clearFieldErrors());
    this.form.addEventListener('change', () => this.calculate(false));
    this.calculate(false);
  }

  parseNumber(input) {
    const normalized = String(input.value || '')
      .trim()
      .replace(/\s/g, '')
      .replace(',', '.');
    const value = Number(normalized);
    return Number.isFinite(value) ? value : NaN;
  }

  clearFieldErrors() {
    this.root.querySelectorAll('[aria-invalid="true"]').forEach((input) => input.removeAttribute('aria-invalid'));
    this.error.hidden = true;
    this.error.textContent = '';
  }

  validate(values) {
    const limits = {
      length: [1, 100],
      width: [0.5, 30],
      depth: [0.1, 15],
      coverage: [1, 30]
    };
    const invalid = [];

    Object.entries(limits).forEach(([name, range]) => {
      if (!Number.isFinite(values[name]) || values[name] < range[0] || values[name] > range[1]) {
        invalid.push(name);
        this.form.elements.namedItem(name).setAttribute('aria-invalid', 'true');
      }
    });

    if (!Number.isFinite(values.factor) || values.factor <= 0 || !Number.isInteger(values.coats) || values.coats < 1 || values.coats > 4) {
      invalid.push('selection');
    }

    return invalid.length === 0;
  }

  calculate(showError) {
    this.clearFieldErrors();
    const values = {
      length: this.parseNumber(this.form.elements.namedItem('length')),
      width: this.parseNumber(this.form.elements.namedItem('width')),
      depth: this.parseNumber(this.form.elements.namedItem('depth')),
      coverage: this.parseNumber(this.form.elements.namedItem('coverage')),
      factor: Number(this.form.elements.namedItem('factor').value),
      coats: Number(this.form.elements.namedItem('coats').value)
    };

    if (!this.validate(values)) {
      if (showError) {
        this.error.textContent = this.locale === 'en'
          ? 'Please check the dimensions and coverage. Only positive, realistic values are accepted.'
          : 'Bitte prüfen Sie Maße und Ergiebigkeit. Es werden nur positive, realistische Werte akzeptiert.';
        this.error.hidden = false;
        this.root.querySelector('[aria-invalid="true"]')?.focus();
      }
      return;
    }

    const rawArea = values.length * (values.width + values.depth) * values.factor;
    const area = Math.ceil(rawArea);
    const litres = area * values.coats / values.coverage;
    const areaText = this.formatNumber.format(area);
    const litresText = this.formatNumber.format(litres);
    const coverageText = this.formatNumber.format(values.coverage);

    this.root.querySelector('[data-area]').textContent = areaText;
    this.root.querySelector('[data-area-detail]').textContent = areaText;
    this.root.querySelector('[data-litres]').textContent = litresText;

    this.root.querySelector('[data-result-copy]').textContent = this.locale === 'en'
      ? `${values.coats} coat${values.coats === 1 ? '' : 's'} at ${coverageText} m²/L require approximately ${litresText} litres for ${areaText} m².`
      : `${values.coats} ${values.coats === 1 ? 'Schicht' : 'Schichten'} bei ${coverageText} m²/L ergeben für ${areaText} m² einen rechnerischen Bedarf von ${litresText} Litern.`;

    this.root.querySelector('[data-formula]').textContent = this.locale === 'en'
      ? `Calculation: ${areaText} m² × ${values.coats} coat${values.coats === 1 ? '' : 's'} ÷ ${coverageText} m²/L = ${litresText} L.`
      : `Berechnung: ${areaText} m² × ${values.coats} ${values.coats === 1 ? 'Schicht' : 'Schichten'} ÷ ${coverageText} m²/L = ${litresText} L.`;
  }
}

const initializeAntifoulingQuantityCalculators = (scope = document) => {
  scope.querySelectorAll('[data-af-quantity-calculator]').forEach((root) => {
    new AntifoulingQuantityCalculator(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeAntifoulingQuantityCalculators(), { once: true });
} else {
  initializeAntifoulingQuantityCalculators();
}

document.addEventListener('shopify:section:load', (event) => initializeAntifoulingQuantityCalculators(event.target));
